import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import _, { capitalize } from 'lodash';
import { Actions, Async, F1 } from '@client/new/helpers/types';
import { Control, UseFormRegister, useForm } from 'react-hook-form';
import { muiForm } from '@client/new/helpers/forms';
import { z } from 'zod';
import { browserHistory } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Exact,
  GetInfluencerQuery,
  useGetInfluencerQuery,
  useSetVisibilityMutation,
  useUpdateCreatorProfileMutation,
  Visibility,
} from 'generated/graphql';
import auth from '@client/components/auth';
import { ApolloQueryResult } from '@apollo/client';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';

type Creator = GetInfluencerQuery['creator'];
export type Influencer = NonNullable<Creator>;

export type SettingsView = {
  dispatch: SettingsViewDispatcher;
  submitting: boolean;
  register: UseFormRegister<InitialForm>;
  control: Control<InitialForm, any, InitialForm>;
  refetch: (
    variables?: Partial<Exact<{ id: number }>> | undefined,
  ) => Promise<ApolloQueryResult<GetInfluencerQuery>>;
  influencer: Influencer;
  showFeatureSettings: boolean;
  oneTimeAction: {
    isExclusive: boolean;
    setExclusive: (isExclusive: boolean) => void;
    submitting: boolean;
    visibility: string;
  };
  canToggleVisibility: boolean;
};

export type SettingsViewActionsMap = {
  submit: null;
  cancel: null;
  setExclusive: boolean;
};

export type SettingsViewActions = Actions<SettingsViewActionsMap>;

export type SettingsViewDispatcher = F1<SettingsViewActions>;

export const settingsForm = z.object({
  email: z
    .string()
    .nonempty('Field is required')
    .email('This is not a valid email'),
});

export type InitialForm = {
  email: string;
};

type SettingsViewModelParams = {
  influencerId: number;
};

export const useSettingsViewModel = ({
  influencerId,
}: SettingsViewModelParams): Async<SettingsView> => {
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, refetch } = useGetInfluencerQuery({
    variables: { id: influencerId },
    fetchPolicy: 'no-cache',
  });

  const [updateCreatorProfileMutation] = useUpdateCreatorProfileMutation();
  const [setVisibilityMutation, setVisibilityMutationState] =
    useSetVisibilityMutation();

  const { register, handleSubmit, control, reset } = muiForm(
    useForm<InitialForm, any, InitialForm>({
      defaultValues: {
        email: '',
      },
      resolver: zodResolver(settingsForm),
    }),
  );

  useEffect(() => {
    if (!_.isEmpty(data?.creator)) {
      reset({
        email: data.creator.email || '',
      });
    }
  }, [data, reset]);

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error || !data?.creator) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const influencer = data?.creator;

  const setVisibility = (isExclusive: boolean) => {
    setVisibilityMutation({
      variables: {
        data: {
          influencerId: influencer.id,
          visibility: isExclusive ? Visibility.Exclusive : Visibility.Premium,
        },
      },
    })
      .then(() => {
        refetch();
        toast.success('Visibility updated successfully!');
      })
      .catch((err) => {
        toast.error(err.message);
      });
  };

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      updateCreatorProfileMutation({
        variables: {
          data: {
            id: influencerId,
            ...values,
          },
        },
      });
      toast.success('Settings updated successfully!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  });

  const dispatch: SettingsViewDispatcher = (action) => {
    switch (action.type) {
      case 'submit':
        onSubmit();
        return null;
      case 'cancel':
        browserHistory.goBack();
        return null;
      case 'setExclusive':
        setVisibility(action.payload);
        return null;
    }
  };

  const showFeatureSettings =
    influencer.businessModel === 'legacy' &&
    (isSubscriptionCreator(influencer?.offering) ||
      hasPremiumFeaturesOTPIds(influencer?.customizations));

  const isExclusive = influencer.visibility === 'exclusive';

  const isAdministrator = auth.isAdministrator();
  const canToggleVisibility =
    isAdministrator && influencer.businessModel === 'legacy';

  return {
    type: 'fetched',
    value: {
      dispatch,
      submitting,
      control,
      register,
      refetch,
      influencer,
      showFeatureSettings,
      oneTimeAction: {
        isExclusive,
        setExclusive: (value: boolean) =>
          dispatch({ type: 'setExclusive', payload: value }),
        submitting: setVisibilityMutationState.loading || loading,
        visibility: capitalize(influencer.visibility),
      },
      canToggleVisibility,
    },
  };
};
